/**
 * useFaceMesh.js
 * Custom React hook that initialises MediaPipe Face Landmarker
 * and runs detection on every animation frame.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

// Pinned, and pinned to the version in package.json rather than to whatever the
// CDN calls latest today. Two reasons:
//
//  1. `@latest` could serve wasm from a different release than the JS bundled
//     here, which is a mismatch nobody would see until it misbehaved.
//  2. The blendshape scores this model produces are *stored* and are meant to
//     let one sitting be compared with the next. On an unpinned model two
//     sittings can be measured by two detectors, and the difference between them
//     would be the library rather than the child.
//
// Bumping it is a deliberate act: record the new version beside the measures, so
// a reader can tell which detector produced which figure.
export const DETECTOR_VERSION = '0.10.35';
export const MODEL_VERSION = 'face_landmarker/float16/1';

const WASM_CDN = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${DETECTOR_VERSION}/wasm`;
const MODEL_URL =
  `https://storage.googleapis.com/mediapipe-models/face_landmarker/${MODEL_VERSION}/face_landmarker.task`;

/**
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {boolean} isActive – start / pause detection
 * @returns {{ landmarks: array|null, isLoaded: boolean }}
 */
export default function useFaceMesh(videoRef, isActive) {
  const [landmarks, setLandmarks] = useState(null);
  const [blendshapes, setBlendshapes] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(-1);

  // ── Initialise the model once ───────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;

    (async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(WASM_CDN);
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          outputFaceBlendshapes: true,
          runningMode: 'VIDEO',
          numFaces: 1,
        });
        if (!cancelled) {
          landmarkerRef.current = landmarker;
          setIsLoaded(true);
        }
      } catch (err) {
        console.error('MediaPipe init failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
      setIsLoaded(false);
    };
  }, [isActive]);

  // ── Per-frame detection loop ────────────────────────────────────────────
  const detect = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;

    if (!landmarker || !video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(detect);
      return;
    }

    const now = performance.now();
    if (now !== lastTimeRef.current) {
      lastTimeRef.current = now;
      const result = landmarker.detectForVideo(video, now);
      if (result.faceLandmarks && result.faceLandmarks.length > 0) {
        setLandmarks(result.faceLandmarks[0]);
        setBlendshapes(result.faceBlendshapes?.[0]?.categories ?? null);
      } else {
        setLandmarks(null);
        setBlendshapes(null);
      }
    }

    rafRef.current = requestAnimationFrame(detect);
  }, [videoRef]);

  useEffect(() => {
    if (isLoaded && isActive) {
      rafRef.current = requestAnimationFrame(detect);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isLoaded, isActive, detect]);

  return { landmarks, blendshapes, isLoaded };
}
